/* eslint-disable no-console */
/* eslint-disable max-len */
import { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, Link } from 'react-router-dom';

import Snackbar, { SnackbarCloseReason } from '@material-ui/core/Snackbar';
import MuiAlert from '@material-ui/lab/Alert';
import {
  LoadingModal,
  ActivaPassivaCard,
  AdviceList,
  LeadConfidence,
  LeadResult,
  formatValuta,
} from '@myfaro/ui-react';
import {
  Box,
  Typography,
  makeStyles,
  Card,
  CardContent,
  useTheme,
} from '@material-ui/core';

import { useProfileContext } from '../../core/profile';
import { Result, Profile, InfoMsg } from '../../core/types';
import { useAppContext } from '../../core/context';
import useCreateOrUpdateLeadResult from '../../core/hooks/useCreateOrUpdateLeadResult';
import { addBaseName } from '../../helpers/index';
import { ResultChart } from '../ResultChart/ResultChart';

const useStyles = makeStyles(theme => ({
  body: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    padding: theme.spacing(4),
  },
  title: {
    marginBottom: theme.spacing(2),
  },
  subTitle: {
    marginBottom: theme.spacing(3),
  },
  section: {
    marginBottom: theme.spacing(3),
  },
  chartTitle: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
}));

const ResultCmp = () => {
  const { t } = useTranslation();
  const { appConfig } = useAppContext();
  const classes = useStyles();
  const theme = useTheme();
  const { useProfile, onChange } = useProfileContext();
  const profile = useProfile<Profile>();
  const localLeadResult = useProfile<Result>('result');
  const initialLeadResult =
    localLeadResult?.isDirty === undefined ? null : localLeadResult;
  const [leadResult, setLeadResult] = useState<Partial<Result> | null>(
    initialLeadResult,
  );
  const [infoMsg, setInfoMsg] = useState<InfoMsg>({
    show: false,
    type: 'error',
    msg: t('result.fetchError'),
  });
  const onChangeResult = onChange('result');
  const onChangeWho = onChange('who');
  const { logoSrc, showLeadConfidence, showResultGraph } = appConfig;
  const history = useHistory();

  const { what, who, goals, contact } = profile;
  // we display email on two pages, after we have leadResult, use the value on WHO
  const currentEmail = leadResult ? who.email || contact.email : contact.email;

  const { isLoading, error } = useCreateOrUpdateLeadResult(
    {
      party: {
        name: who.name,
        dateOfBirth: who.dateOfBirth,
        locale: 'NL',
        socialStatus: who.socialStatus,
        civilStatus: who.civilStatus,
        hasOwnCompany: who.hasOwnCompany,
        gender: who.gender,
        zip_code: who.zip_code,
        hasDependentChildren: who.hasDependentChildren,
      },
      inventory: {
        balanceSheet: {
          bankSavingsValue: what.balanceSheet?.bankSavingsValue,
          bankInvestmentsValue: what.balanceSheet?.bankInvestmentsValue,
          insuranceProtectionValue: what.balanceSheet?.insuranceProtectionValue,
          insuranceInvestmentsValue:
            what.balanceSheet?.insuranceInvestmentsValue,
          homeValue: what.balanceSheet?.homeValue,
          otherRealEstateValue: what.balanceSheet?.otherRealEstateValue,
          businessAssetsValue: what.balanceSheet?.businessAssetsValue,
          liabilitiesValue: what.balanceSheet?.liabilitiesValue,
          liabilitiesRemainingAtRetirement:
            what.balanceSheet?.liabilitiesRemainingAtRetirement,
          savingsInvestmentContracts:
            what.balanceSheet?.savingsInvestmentContracts,
          monthlyExpensesLiabilitiesValue:
            what.balanceSheet?.monthlyExpensesLiabilitiesValue,
          liabilitiesDurationInYears:
            what.balanceSheet?.liabilitiesDurationInYears,
        },
        cashFlow: {
          monthlySavingsValue: goals.monthlySavingsValue,
        },
      },
      scenario: {
        retirement: {
          retirementAge: goals.retirement?.retirementAge ?? 63,
          yearsBeforeRetirementAge: goals.retirement?.yearsBeforeRetirementAge,
          plannedEndAge: goals.retirement?.plannedEndAge,
          desiredRetirementBudgetValue:
            goals.retirement?.desiredRetirementBudgetValue,
          desiredEndCapitalValue: goals.retirement?.desiredEndCapitalValue,
        },
        notes: goals.notes,
        additionalDomains: goals.additionalDomains,
      },
      contactDetails: {
        name: who.name,
        email: currentEmail,
        mobile: leadResult ? who.mobile : contact.mobile,
      },
    },
    leadResult,
    nextLeadResult => {
      // we want to set the savedEmail so that we can differentiate
      // between the PUT and POST requests in the future
      localStorage.setItem('savedEmail', currentEmail || '');
      // change global state, this way it also gets saved in the localStorage
      onChangeResult(nextLeadResult);
      setLeadResult(nextLeadResult);
    },
  );

  useEffect(() => {
    if (error) {
      // There is an existing lead linked to the provided mail address
      if (error.fields === 'EXISTING LEAD') {
        setInfoMsg(state => ({
          ...state,
          show: true,
          msg: <span>{t('result.existingUser')}</span>,
        }));
        setTimeout(() => {
          history.push({
            pathname: leadResult ? '/who' : '/contact',
            state: {
              type: 'existingAccountError',
              content: t('errors.existingUser'),
              link: '/',
            },
          });
        }, 2000);
      } else if (error.fields === 'EXISTING CASE FILE') {
        setInfoMsg(state => ({
          ...state,
          show: true,
          msg: <span>{t('result.existingDossier')}</span>,
        }));
        setTimeout(() => {
          history.push({
            pathname: leadResult ? '/who' : '/contact',
            state: {
              type: 'existingDossierError',
            },
          });
        }, 2000);
      } else if (error.fields === 'INVALID TOKEN') {
        setInfoMsg(state => ({
          ...state,
          show: true,
          msg: <span>{t('result.invalidToken')}</span>,
        }));

        setTimeout(() => {
          history.push({
            pathname: '/who',
            state: {
              type: 'invalidTokenError',
            },
          });
        }, 2000);
        // the broker's plan is not valid anymore
        // redirect to homepage
      } else if (error.fields === 'BROKER NOT REGISTERED') {
        setInfoMsg(state => ({
          ...state,
          show: true,
          msg: <span>{t('result.brokerNotRegistered')}</span>,
        }));
        setTimeout(() => {
          window.location.href = '/';
        }, 10000);
      } else {
        setInfoMsg(state => ({
          ...state,
          show: true,
          msg: (
            <span>
              {t('result.fetchError')}{' '}
              <Link to={{ pathname: '/', state: { type: 'prefillEmail' } }}>
                {t('result.fetchErrorLink')}
              </Link>{' '}
              {t('result.fetchError2')}
            </span>
          ),
        }));
      }
    }
  }, [error]);

  const parseItems = (items: object): any[] =>
    Object.values(items).map(({ label, value }) => ({
      label,
      amount: Math.abs(value),
    })) ?? [];
  const activaItems = useMemo(
    () => parseItems(leadResult?.balanceSheetAtRetirement?.assets ?? {}),
    [leadResult],
  );
  const passivaItems = useMemo(
    () => parseItems(leadResult?.balanceSheetAtRetirement?.liabilities ?? {}),
    [leadResult],
  );
  const calculateTotal = (items: any[]): string =>
    items.map(({ amount }) => amount).reduce((acc, curr) => acc + curr, 0);
  const activaTotal = useMemo(() => Number(calculateTotal(activaItems)), [
    activaItems,
  ]);
  const passivaTotal = useMemo(() => Number(calculateTotal(passivaItems)), [
    passivaItems,
  ]);
  const mapColorToLeadResultStatus = (
    color: string,
  ): 'bad' | 'caution' | 'good' => {
    if (color === 'red') {
      return 'bad';
    }
    if (color === 'orange') {
      return 'caution';
    }
    return 'good';
  };

  // TODO: replace this with the snackbar provider, like the one we have in web app
  const handleClose = (reason?: SnackbarCloseReason) => {
    if (reason === 'clickaway') {
      return;
    }
    setInfoMsg(state => {
      return {
        ...state,
        show: false,
      };
    });
  };

  return (
    <>
      {/* TODO: replace with snackbar provider */}
      <Snackbar
        open={infoMsg.show}
        autoHideDuration={6000}
        onClose={(event, reason) => handleClose(reason)}>
        <MuiAlert
          elevation={6}
          onClose={() => handleClose()}
          variant='filled'
          severity={infoMsg.type}>
          {infoMsg.msg}
        </MuiAlert>
      </Snackbar>

      {isLoading || leadResult == null ? (
        <LoadingModal
          isDialogOpen
          title={t('results.loading.title')}
          logoSrc={addBaseName(logoSrc)}
        />
      ) : (
        <Box className={classes.body}>
          <Typography variant='h5' className={classes.title}>
            {t('results.title')}
          </Typography>
          <Typography
            variant='body2'
            color={'textSecondary'}
            className={classes.subTitle}>
            {t('results.subTitle')}
          </Typography>
          <Box mb={2}>
            <Card>
              <CardContent>
                <ActivaPassivaCard
                  headerTitle={t('results.activaPassiva.title')}
                  activaTitle={t('label.activa')}
                  passivaTitle={t('label.passiva')}
                  activaItems={activaItems
                    .filter(a => a.amount !== 0)
                    .map(item => ({
                      ...item,
                      amount: formatValuta(item.amount),
                    }))}
                  passivaItems={passivaItems
                    .filter(a => a.amount !== 0)
                    .map(item => ({
                      ...item,
                      amount: formatValuta(item.amount),
                    }))}
                  activaTotal={formatValuta(activaTotal)}
                  passivaTotal={formatValuta(passivaTotal)}
                />
              </CardContent>
            </Card>
          </Box>
          {showResultGraph && (
            <>
              <Box my={2}>
                <Typography
                  variant='body2'
                  color={'textSecondary'}
                  className={classes.subTitle}>
                  {t('results.graphText')}
                </Typography>
              </Box>
              <Box mb={2}>
                <Card>
                  <CardContent>
                    <Typography className={classes.chartTitle} variant='h6'>
                      Pensioen
                    </Typography>
                    <ResultChart
                      inputs={{
                        labels: leadResult.liquidAssetsGraph.labels,
                        data: leadResult.liquidAssetsGraph.data,
                      }}
                      status={mapColorToLeadResultStatus(
                        leadResult?.color ?? '',
                      )}
                    />
                  </CardContent>
                </Card>
              </Box>
            </>
          )}
          {showLeadConfidence && (
            <Box mb={4}>
              <LeadConfidence
                percent={(leadResult?.confidenceScore ?? 0) / 100}
                title={t('results.reliabilityIndicator.title')}
                colors={[
                  theme.palette.primary.light,
                  theme.palette.primary.main,
                  theme.palette.primary.dark,
                ]}
                tooltipText={t('results.reliabilityIndicator.tooltip')}
              />
            </Box>
          )}
          <Typography className={classes.subTitle} variant='subtitle2'>
            <LeadResult
              status={mapColorToLeadResultStatus(leadResult?.color ?? '')}
              label={leadResult?.actionTitle}
            />
          </Typography>
          <Typography className={classes.section} variant='body2'>
            {leadResult?.actionIntro}
          </Typography>
          {leadResult?.actionImageUrl && (
            <img src={leadResult?.actionImageUrl} alt='results-graphic' />
          )}
          <AdviceList
            actionsTitle={leadResult?.actionDetailsTitle ?? ''}
            actions={
              leadResult?.actionDetails?.map(
                ({ actionDescription, actionUrl }) => ({
                  label: actionDescription,
                  url: actionUrl,
                }),
              ) ?? []
            }
            advicesTitle={leadResult?.domainFollowUpTitle}
            advices={
              leadResult?.domainFollowUpDetails?.map(
                ({ domainDescription, domainUrl }: any) => ({
                  label: domainDescription,
                  url: domainUrl,
                }),
              ) ?? []
            }
          />
        </Box>
      )}
    </>
  );
};

export default ResultCmp;
